import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  Pagination,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { InfoLabel } from '@/components/common';
import { COURSE_TYPE_TABS, LANGUAGE_OPTIONS } from '@/constants/courses';
import { useCourseList } from '@/hooks/courses.hook';
import CourseSelectLabel from './CourseSelectLabel';

const COURSE_PAGE_SIZE = 10;

const toCourseMapItem = (course) => ({
  courseId: course.courseId,
  courseName: course.courseName || `과정 ID: ${course.courseId}`,
  freeYn: course.freeYn,
  hasTraining: course.hasTraining,
  languageName: course.languageName,
  languageId: course.languageId,
  courseType: course.courseType,
});

/** 선택된 과정 카드에 넣을 한 줄 요약 */
const buildSelectedCourseMeta = (course) => {
  if (!course) return '';
  const languageName =
    course.languageName
    || LANGUAGE_OPTIONS.find((option) => option.languageId === course.languageId)?.languageName
    || '';
  return [
    languageName,
    course.freeYn ? '무료' : '유료',
    course.hasTraining ? '훈련 필요' : '훈련 불필요',
  ].filter(Boolean).join(' · ');
};

/**
 * 그룹 등록/상세에서 쓰는 과정 선택 패널.
 * courseType 탭으로 목록을 나누고, 탭이 바뀌어도 선택 상태는 유지한다.
 */
const CourseSelectPanel = ({ control, name, initialCourses = [], onSelectedCoursesChange }) => {
  const [courseType, setCourseType] = useState(COURSE_TYPE_TABS[0].value);
  const [coursePage, setCoursePage] = useState(1);
  const [selectedCoursesMap, setSelectedCoursesMap] = useState({});
  const didSeedInitialCourses = useRef(false);

  const selectedIdsRaw = useWatch({ control, name });
  const selectedIds = Array.isArray(selectedIdsRaw) ? selectedIdsRaw : [];

  const { data: courseListData, isLoading: isCourseListLoading } = useCourseList({
    page: coursePage,
    size: COURSE_PAGE_SIZE,
    hasContent: true,
    courseType,
  });

  const courseList = courseListData?.list || courseListData?.data || courseListData || [];
  const courseTotal = courseListData?.total || 0;
  const courseTotalPages = Math.ceil(courseTotal / COURSE_PAGE_SIZE);

  // 상세 화면에서 이미 배정된 과정 정보를 한 번만 시드한다.
  useEffect(() => {
    if (didSeedInitialCourses.current || !initialCourses.length) return;
    didSeedInitialCourses.current = true;
    setSelectedCoursesMap((prev) => {
      const next = { ...prev };
      initialCourses.forEach((course) => {
        if (course.courseId != null && !next[course.courseId]) {
          next[course.courseId] = toCourseMapItem(course);
        }
      });
      return next;
    });
  }, [initialCourses]);

  // 현재 탭 목록에 있는 선택 과정을 맵에 채워, 페이지/탭 이동 후에도 칩을 유지한다.
  useEffect(() => {
    if (!courseList.length || selectedIds.length === 0) return;
    setSelectedCoursesMap((prev) => {
      let changed = false;
      const next = { ...prev };
      courseList.forEach((course) => {
        if (selectedIds.includes(course.courseId) && !next[course.courseId]) {
          next[course.courseId] = toCourseMapItem(course);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [courseList, selectedIds]);

  // 부모에서 수료 조건 검증에 쓰도록 선택 과정 정보를 전달한다.
  useEffect(() => {
    if (!onSelectedCoursesChange) return;
    onSelectedCoursesChange(
      selectedIds
        .map((id) => selectedCoursesMap[id])
        .filter(Boolean)
    );
  }, [selectedIds, selectedCoursesMap, onSelectedCoursesChange]);

  const handleTabChange = useCallback((_, nextType) => {
    setCourseType(nextType);
    setCoursePage(1);
  }, []);

  const handleCoursePageChange = useCallback((_, value) => {
    setCoursePage(value);
  }, []);

  const handleCourseToggle = useCallback((courseId, currentList, onChange, course) => {
    const courseIdNum = Number(courseId);
    const isSelected = currentList.includes(courseIdNum);

    if (isSelected) {
      onChange(currentList.filter((id) => id !== courseIdNum));
      setSelectedCoursesMap((prev) => {
        const next = { ...prev };
        delete next[courseIdNum];
        return next;
      });
      return;
    }

    onChange([...currentList, courseIdNum]);
    if (course) {
      setSelectedCoursesMap((prev) => ({
        ...prev,
        [courseIdNum]: toCourseMapItem(course),
      }));
    }
  }, []);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => {
        const currentIds = Array.isArray(value) ? value : [];

        return (
          <>
            <Tabs
              value={courseType}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 36,
                mb: 1.5,
                '& .MuiTab-root': {
                  minHeight: 36,
                  minWidth: 0,
                  px: 1.25,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                },
              }}
            >
              {COURSE_TYPE_TABS.map((tab) => (
                <Tab key={tab.value} value={tab.value} label={tab.label} />
              ))}
            </Tabs>

            <FormGroup>
              {isCourseListLoading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : courseList.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  이 유형의 공개된 과정이 없습니다.
                </Typography>
              ) : (
                courseList.map((course) => (
                  <FormControlLabel
                    key={course.courseId}
                    control={
                      <Checkbox
                        checked={currentIds.includes(course.courseId)}
                        onChange={() => handleCourseToggle(course.courseId, currentIds, onChange, course)}
                      />
                    }
                    label={<CourseSelectLabel course={course} />}
                  />
                ))
              )}
            </FormGroup>

            {courseTotalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={courseTotalPages}
                  page={coursePage}
                  onChange={handleCoursePageChange}
                  color="primary"
                  size="small"
                />
              </Box>
            )}

            {currentIds.length > 0 && (
              <Box mt={2} pt={2} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                  <InfoLabel sx={{ mb: 0 }}>선택된 과정</InfoLabel>
                  <Typography variant="caption" color="primary" fontWeight={700}>
                    {currentIds.length}
                  </Typography>
                </Stack>
                <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
                  {currentIds.map((courseId) => {
                    const course = selectedCoursesMap[courseId];
                    const courseName = course?.courseName || `과정 ID: ${courseId}`;
                    const meta = buildSelectedCourseMeta(course);

                    return (
                      <Box
                        key={courseId}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.5,
                          px: 1.25,
                          py: 1,
                          borderRadius: 1.5,
                          bgcolor: '#f5f9ff',
                          border: '1px solid',
                          borderColor: '#bbdefb',
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            noWrap
                            title={courseName}
                          >
                            {courseName}
                          </Typography>
                          {meta && (
                            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.25 }}>
                              {meta}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          aria-label={`${courseName} 선택 해제`}
                          onClick={() => handleCourseToggle(courseId, currentIds, onChange, course)}
                          sx={{ mt: -0.25, color: 'text.secondary' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
            {currentIds.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                과정을 선택하지 않았습니다.
              </Typography>
            )}
          </>
        );
      }}
    />
  );
};

export default CourseSelectPanel;
